/**
 * Race Timeline — every clear, sabotage firing, disruption and finish, in order.
 *
 * Times are shown as an offset from the game's start rather than a wall clock,
 * which is what an admin reviewing a race actually reasons in ("they were stuck
 * for twenty minutes at +1h"). Only events with a trustworthy clock reach this
 * list — the snapshot withholds disruptions stamped outside the game window,
 * since a point 22 hours before the start would either stretch the axis or
 * quietly imply the race ran all night.
 */

import type { AdminTrlResultsResponse, TrlTimelineEventType } from '@swtorbit/shared-socket-events';
import { StatCard } from './StatCard';
import { formatOffset } from './statsFormat';

const TYPE_ICONS: Record<TrlTimelineEventType, string> = {
  planet_cleared: '🪐',
  sabotage_fired: '💣',
  disruption: '✨',
  team_finished: '🏁',
};

export function TrlTimelineCard({ results }: { results: AdminTrlResultsResponse }) {
  const { timeline, startedAtUtcMs } = results;

  return (
    <StatCard
      title="Race Timeline"
      icon="📜"
      summary={`${timeline.length} events`}
      isEmpty={timeline.length === 0}
      emptyMessage="Nothing happened in this game yet."
    >
      <div className="d-flex flex-column gap-2xs" style={{ maxHeight: '28rem', overflowY: 'auto' }}>
        {timeline.map((ev, idx) => (
          <div
            key={`${ev.utcMs}:${ev.type}:${ev.teamId}:${idx}`}
            className="d-flex gap-xs align-items-center fs-sm flex-wrap"
          >
            <span className="text-slate fs-xs" style={{ minWidth: '5.5rem' }}>
              {formatOffset(ev.utcMs, startedAtUtcMs)}
            </span>
            <span style={{ minWidth: '1.5rem' }}>{TYPE_ICONS[ev.type]}</span>
            <span className="text-white flex-1">{ev.label}</span>
            {ev.detail && <span className="fs-xs text-slate">{ev.detail}</span>}
          </div>
        ))}
      </div>
    </StatCard>
  );
}
